import { LOGOUT, RECEIVE_INITIAL_STATE } from "@/components/access/ducks/Actions"
import getIsAdminFromResponse from "@/components/helpers/getIsAdminFromResponse"
import {
  ADD_NOTE,
  EDIT_NOTE,
  FETCH_NOTES,
  RECEIVE_CURRENT_CONSTITUENT,
  RECEIVE_DELETED_NOTE,
  RECEIVE_EDITED_NOTE,
  RECEIVE_NOTE,
  RECEIVE_NOTES,
  TOGGLE_ADMIN_USER_EDIT,
  UPDATE_CONSTITUENT_CHANGES_PENDING,
} from "@/components/profile/admin/ducks/Actions"
import { CLEAR_SELECTED_USER } from "@/components/search/ducks/Actions"

/**
 * @typedef {Object} ProfileAdminState
 * @property {Object} currentConstituent - Current constituent data
 * @property {boolean} loadingConstituents - Whether constituents are loading
 * @property {boolean} changesPending - Whether there are pending changes
 * @property {boolean} adminUserEdit - Whether admin user edit mode is enabled
 * @property {Array} notes - Admin notes
 * @property {boolean} loadingNotes - Whether notes are loading
 * @property {boolean} addingNote - Whether a note is being added
 * @property {boolean} updatingNote - Whether a note is being updated
 * @property {boolean} deleteNote - Whether a note is being deleted
 */

const defaultState = {
  currentConstituent: {},
  loadingConstituents: false,
  changesPending: false,
  adminUserEdit: false,
  notes: [],
  loadingNotes: false,
  addingNote: false,
  updatingNote: false,
  deleteNote: false,
}

/**
 * Profile admin reducer function
 * @param {ProfileAdminState} oldState - Previous state
 * @param {Object} action - Redux action
 * @returns {ProfileAdminState} New state
 */
const adminProfileReducer = (oldState = defaultState, action) => {
  Object.freeze(oldState)
  switch (action.type) {
    case RECEIVE_INITIAL_STATE: {
      const isAdmin = getIsAdminFromResponse(action)

      if (isAdmin) {
        return defaultState
      }
      return {}
    }

    case LOGOUT:
      return {}

    case RECEIVE_CURRENT_CONSTITUENT:
      return {
        ...oldState,
        currentConstituent: action.constituent,
        loadingConstituents: false,
      }

    case UPDATE_CONSTITUENT_CHANGES_PENDING:
      return {
        ...oldState,
        changesPending: action.pending,
      }

    case TOGGLE_ADMIN_USER_EDIT:
      return {
        ...oldState,
        adminUserEdit: !oldState.adminUserEdit,
      }

    case CLEAR_SELECTED_USER:
      return {
        ...oldState,
        adminUserEdit: false,
      }

    case FETCH_NOTES: {
      return {
        ...oldState,
        loadingNotes: true,
      }
    }

    case RECEIVE_NOTES: {
      return {
        ...oldState,
        notes: action.notes?.notes,
        loadingNotes: false,
      }
    }

    case ADD_NOTE: {
      return {
        ...oldState,
        addingNote: true,
      }
    }

    case RECEIVE_NOTE: {
      if (action.payload.shouldReload) {
        // reload window
        window.location.reload()
        return oldState
      }

      if (action.payload.note) {
        return {
          ...oldState,
          notes: [action.payload.note, ...oldState.notes],
          addingNote: false,
        }
      }

      return {
        ...oldState,
        addingNote: false,
      }
    }

    case EDIT_NOTE: {
      return {
        ...oldState,
        updatingNote: true,
      }
    }
    case RECEIVE_EDITED_NOTE: {
      const { note, shouldReload } = action.payload
      if (shouldReload) {
        window.location.reload()
        return oldState
      }

      const oldNoteIndex = oldState.notes.findIndex((n) => n.noteId === note.noteId)
      const notes = [...oldState.notes]
      notes[oldNoteIndex] = note
      return {
        ...oldState,
        updatingNote: false,
        notes,
      }
    }

    case RECEIVE_DELETED_NOTE: {
      console.debug("RECEIVE_DELETED_NOTE", action.payload)
      const { noteId, shouldReload } = action.payload

      const filtered = oldState.notes.filter((n) => n.noteId !== noteId)
      if (shouldReload) {
        window.location.reload()
        return oldState
      }

      return {
        ...oldState,
        deleteNote: true,
        notes: filtered,
      }
    }

    default:
      return oldState
  }
}

export default adminProfileReducer
